/**
 * Measurement Template Editor Component
 * Edits an array of measurement templates (measurand + uncertainty)
 */

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import type { MeasurementTemplate } from '../types';

interface MeasurementTemplateEditorProps {
  value: MeasurementTemplate[];
  onChange: (templates: MeasurementTemplate[]) => void;
  disabled?: boolean;
}

export function MeasurementTemplateEditor({
  value,
  onChange,
  disabled,
}: MeasurementTemplateEditorProps) {
  const handleAdd = () => {
    onChange([...value, { measurand: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: keyof MeasurementTemplate, fieldValue: string) => {
    const updated = value.map((template, i) => {
      if (i !== index) return template;
      if (field === 'target_measurement_uncertainty') {
        return {
          ...template,
          [field]: fieldValue ? Number(fieldValue) : undefined,
        };
      }
      return { ...template, [field]: fieldValue };
    });
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Measurement Templates</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={disabled}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add
        </Button>
      </div>

      {value.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No measurement templates defined.
        </p>
      )}

      {value.map((template, index) => (
        <div key={index} className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            {index === 0 && (
              <Label className="text-xs text-muted-foreground">Measurand</Label>
            )}
            <Input
              value={template.measurand}
              onChange={(e) => handleChange(index, 'measurand', e.target.value)}
              placeholder="e.g. Temperature"
              disabled={disabled}
            />
          </div>
          <div className="w-40 space-y-1">
            {index === 0 && (
              <Label className="text-xs text-muted-foreground">Uncertainty</Label>
            )}
            <Input
              type="number"
              step="0.01"
              value={template.target_measurement_uncertainty ?? ''}
              onChange={(e) =>
                handleChange(index, 'target_measurement_uncertainty', e.target.value)
              }
              placeholder="0.05"
              disabled={disabled}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleRemove(index)}
            disabled={disabled}
            className="h-9 w-9 p-0 shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}
